import {
  TrackHistoryRequestInputSchema,
  TRACK_HISTORY_REQUEST_TOOL_NAME,
} from '@terra/shared-dto-chat-types';
import { terraProxyApiClient } from '../../../../clients/testerClient';
import {
  buildHarPostData,
  buildHarResponseContent,
  computePortalBodyByteSize,
  normalizeHarHttpVersion,
} from '@terra/shared-utils';
import type { RequestMetadataRow } from '../../../../electron/search-db';
import {
  getElectronApiUnavailableResult,
  isElectronApiAvailable,
  parsePortalToolHandlerInput,
  resolvePortalTargetScope,
} from '../portal-tool-handler.utils';
import type { PortalToolHandler } from '../types';

function parseHeadersFromJson(
  raw: string | null,
): Record<string, string | string[]> {
  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw) as unknown;

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }

    const headers: Record<string, string | string[]> = {};

    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'string') {
        headers[key] = value;
      } else if (Array.isArray(value)) {
        headers[key] = value.map(String);
      }
    }

    return headers;
  } catch (error: unknown) {
    console.warn(
      '[trackHistoryRequestHandler] Failed to parse headers from JSON',
      {
        error: error instanceof Error ? error : new Error(String(error)),
      },
    );

    return {};
  }
}

function toHarHeaderPairs(
  headers: Record<string, string | string[]>,
): Array<{ name: string; value: string }> {
  return Object.entries(headers).flatMap(([name, value]) => {
    if (Array.isArray(value)) {
      return value.map((v) => ({ name, value: v }));
    }

    return [{ name, value }];
  });
}

function calculateHeadersSize(
  headers: Array<{ name: string; value: string }>,
): number {
  return headers.reduce(
    (size, header) => size + header.name.length + header.value.length + 4,
    0,
  );
}

function parseDurationMs(timing: string | null): number {
  if (!timing) {
    return 0;
  }

  try {
    const parsed = JSON.parse(timing) as unknown;

    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const obj = parsed as Record<string, unknown>;

      if (typeof obj.total === 'number' && isFinite(obj.total)) {
        return obj.total;
      }

      if (typeof obj.duration === 'number' && isFinite(obj.duration)) {
        return obj.duration;
      }
    }
  } catch {
    // Not JSON — ignore
  }

  const num = Number(timing);

  return isFinite(num) ? num : 0;
}

function buildHarPayloadFromRow({
  row,
  requestBody,
  responseBody,
  applicationId,
  credentialId,
}: {
  row: RequestMetadataRow;
  requestBody: string;
  responseBody: string;
  applicationId: string;
  credentialId?: string;
}) {
  const requestHeaders = parseHeadersFromJson(row.request_headers);
  const responseHeaders = parseHeadersFromJson(row.response_headers);
  const requestHarHeaders = toHarHeaderPairs(requestHeaders);
  const responseHarHeaders = toHarHeaderPairs(responseHeaders);
  const httpVersion = normalizeHarHttpVersion(row.http_version ?? undefined);
  const responseHttpVersion = normalizeHarHttpVersion(
    row.response_http_version ?? row.http_version ?? undefined,
  );

  let queryString: Array<{ name: string; value: string }> = [];

  try {
    const url = new URL(row.url);

    queryString = Array.from(url.searchParams.entries()).map(
      ([name, value]) => ({
        name,
        value,
      }),
    );
  } catch {
    // Invalid URL, skip query string parsing
  }

  const responseMimeType = row.response_body_content_type ?? '';

  const builtResponseContent = buildHarResponseContent({
    body: responseBody,
    headers: responseHeaders,
  });

  return {
    applicationId,
    credentialId,
    pageUrl: undefined,
    endpointSource: 'TRACKING' as const,
    request: {
      method: row.method,
      url: row.url,
      httpVersion,
      cookies: [],
      headers: requestHarHeaders,
      queryString,
      postData: buildHarPostData({
        body: requestBody,
        headers: requestHeaders,
      }),
      headersSize: calculateHeadersSize(requestHarHeaders),
      bodySize: computePortalBodyByteSize(requestBody),
    },
    response: {
      status: row.status_code ?? 0,
      statusText: row.status_message ?? '',
      httpVersion: responseHttpVersion,
      cookies: [],
      headers: responseHarHeaders,
      content: builtResponseContent
        ? {
            size: builtResponseContent.size,
            mimeType: builtResponseContent.mimeType,
            text: builtResponseContent.text,
            ...(builtResponseContent.encoding
              ? { encoding: builtResponseContent.encoding }
              : {}),
          }
        : {
            size: 0,
            mimeType: responseMimeType,
            text: null,
          },
      redirectURL: '',
      headersSize: calculateHeadersSize(responseHarHeaders),
      bodySize: builtResponseContent?.size ?? 0,
    },
    timings: {
      blocked: -1,
      dns: -1,
      connect: -1,
      send: 0,
      wait: parseDurationMs(row.timing),
      receive: 0,
    },
  };
}

export const trackHistoryRequestHandler: PortalToolHandler = async ({
  input,
  toolCallId,
  batchId,
  applicationId,
}) => {
  const parsedInput = parsePortalToolHandlerInput({
    schema: TrackHistoryRequestInputSchema,
    input,
    toolName: TRACK_HISTORY_REQUEST_TOOL_NAME,
    toolCallId,
    batchId,
  });

  if (!parsedInput.ok) {
    return parsedInput.result;
  }

  if (!isElectronApiAvailable() || !window.electronAPI.history?.getRows) {
    return getElectronApiUnavailableResult({
      toolName: TRACK_HISTORY_REQUEST_TOOL_NAME,
      toolCallId,
      batchId,
      message: 'History API is not available',
    });
  }

  const { targetId, targetType } = resolvePortalTargetScope({ applicationId });
  const { requestId, credentialId, allowNotFoundResponse } = parsedInput.data;

  const historyResult = await window.electronAPI.history.getRows({
    targetId,
    targetType,
    startRow: 0,
    endRow: 1,
    sortModel: [{ colId: 'timestamp', sort: 'desc' }],
    filters: {
      tqlWhereClause: 'r.id = ?',
      tqlParams: [requestId],
    },
  });

  if (!historyResult.success || !historyResult.data?.rows[0]) {
    return {
      status: 'error',
      result: {
        message: `Request ${requestId} not found in proxy history`,
      },
    };
  }

  const row = historyResult.data.rows[0];

  if (row.was_proxy_modified === 1) {
    return {
      status: 'error',
      result: {
        message: `Request ${requestId} was modified by the proxy and cannot be tracked`,
        data: {
          requestId,
          tracked: false,
          skipped: true,
        },
      },
    };
  }

  if (row.status_code === 404 && !allowNotFoundResponse) {
    return {
      status: 'error',
      result: {
        message: `Request ${requestId} returned 404 and was not tracked. Set allowNotFoundResponse only when the user explicitly asked to track this 404.`,
        data: {
          requestId,
          tracked: false,
          skipped: true,
        },
      },
    };
  }

  const bodyResult = await window.electronAPI.bodyDb.get(requestId);

  if (!bodyResult.success) {
    console.warn(
      '[trackHistoryRequestHandler] Failed to load request/response bodies',
      {
        requestId,
        error: bodyResult.error,
      },
    );

    return {
      status: 'error',
      result: {
        message:
          bodyResult.error ??
          `Failed to load request/response bodies for ${requestId}`,
        data: {
          requestId,
          tracked: false,
        },
      },
    };
  }

  const requestBody = bodyResult.data?.requestBody ?? '';
  const responseBody = bodyResult.data?.responseBody ?? '';

  const harPayload = {
    ...buildHarPayloadFromRow({
      row,
      requestBody,
      responseBody,
      applicationId: targetId,
      credentialId,
    }),
    ...(allowNotFoundResponse ? { allowNotFoundResponse: true } : {}),
  };

  let trackResult: { success: boolean; message?: string };

  try {
    trackResult = await terraProxyApiClient.track(harPayload);
  } catch (error: unknown) {
    return {
      status: 'error',
      result: {
        message:
          error instanceof Error ? error.message : 'Failed to track request',
      },
    };
  }

  if (!trackResult.success) {
    return {
      status: 'error',
      result: {
        message: trackResult.message ?? `Request ${requestId} was not tracked`,
        data: {
          requestId,
          tracked: false,
          skipped: true,
        },
      },
    };
  }

  return {
    status: 'success',
    result: {
      message: `Request ${requestId} tracked successfully`,
      data: {
        requestId,
        tracked: true,
      },
    },
  };
};
